/**
 * English Test Verification Dashboard (CMS)
 * Counselors can view pending verifications and approve/reject them
 */
import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  Loader2,
  Search,
  RefreshCw,
  Eye,
  Shield,
  AlertCircle,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useCMSAuth } from '@/context/CMSAuthContext';

const API_URL = window.location.origin;

const EnglishVerificationDashboard = () => {
  const { user: adminUser, token } = useCMSAuth();
  
  const [loading, setLoading] = useState(true);
  const [verifications, setVerifications] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [portals, setPortals] = useState({});
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  const [testTypeFilter, setTestTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Detail Dialog
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Decision Dialog
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [decisionType, setDecisionType] = useState(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [verifiedScores, setVerifiedScores] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Clipboard state
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    loadVerifications();
  }, [statusFilter, testTypeFilter]);

  const loadVerifications = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/english-verification/all?limit=100`;
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (testTypeFilter !== 'all') {
        url += `&test_type=${testTypeFilter}`;
      }
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setVerifications(data.verifications || []);
        setStats(data.stats || { pending: 0, approved: 0, rejected: 0 });
        setPortals(data.portals || {});
      }
    } catch (error) {
      console.error('Error loading verifications:', error);
      toast.error('Failed to load verifications');
    }
    setLoading(false);
  };

  const handleViewDetail = (verification) => {
    setSelectedVerification(verification);
    setShowDetailDialog(true);
  };

  const handleStartDecision = (type) => {
    setDecisionType(type);
    setDecisionNotes('');
    // Pre-populate verified scores with claimed scores
    if (selectedVerification?.claimed_scores) {
      setVerifiedScores({ ...selectedVerification.claimed_scores });
    }
    setShowDecisionDialog(true);
  };

  const handleSubmitDecision = async () => {
    if (!selectedVerification || !decisionType) return;
    
    setSubmitting(true);
    try {
      const payload = {
        decision: decisionType,
        counselor_notes: decisionNotes || null,
        verified_scores: decisionType === 'approved' ? verifiedScores : null
      };
      
      const res = await fetch(
        `${API_URL}/api/english-verification/${selectedVerification.id}/decide?counselor_id=${adminUser?.id || 'admin'}&counselor_name=${encodeURIComponent(adminUser?.name || 'Admin')}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`Verification ${decisionType} successfully!`);
        setShowDecisionDialog(false);
        setShowDetailDialog(false);
        setSelectedVerification(null);
        loadVerifications();
      } else {
        toast.error(data.detail || 'Failed to submit decision');
      }
    } catch (error) {
      console.error('Decision error:', error);
      toast.error('Failed to submit decision');
    }
    setSubmitting(false);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Verified</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return null;
    }
  };

  // Filter verifications by search query
  const filteredVerifications = verifications.filter(v => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      v.student_email?.toLowerCase().includes(query) ||
      v.student_name?.toLowerCase().includes(query) ||
      v.trf_number?.toLowerCase().includes(query) ||
      v.score_report_code?.toLowerCase().includes(query)
    );
  });

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">English Test Verification</h1>
            <p className="text-sm text-slate-500 mt-1">
              Verify student IELTS and PTE scores using official portals
            </p>
          </div>
          <Button onClick={loadVerifications} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div 
            className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${statusFilter === 'pending' ? 'ring-2 ring-amber-400' : ''}`}
            onClick={() => setStatusFilter('pending')}
            data-testid="pending-stats-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.pending}</p>
                <p className="text-sm text-slate-500">Pending</p>
              </div>
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${statusFilter === 'approved' ? 'ring-2 ring-emerald-400' : ''}`}
            onClick={() => setStatusFilter('approved')}
            data-testid="approved-stats-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.approved}</p>
                <p className="text-sm text-slate-500">Approved</p>
              </div>
            </div>
          </div>
          <div 
            className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${statusFilter === 'rejected' ? 'ring-2 ring-red-400' : ''}`}
            onClick={() => setStatusFilter('rejected')}
            data-testid="rejected-stats-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.rejected}</p>
                <p className="text-sm text-slate-500">Rejected</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4 mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by email, name, TRF number, or score report code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <Select value={testTypeFilter} onValueChange={setTestTypeFilter}>
            <SelectTrigger className="w-[150px]" data-testid="test-type-filter">
              <SelectValue placeholder="Test Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tests</SelectItem>
              <SelectItem value="IELTS">IELTS</SelectItem>
              <SelectItem value="PTE">PTE</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
            size="sm"
            data-testid="show-all-btn"
          >
            Show All
          </Button>
        </div>

        {/* Verification Portal Info */}
        {statusFilter === 'pending' && stats.pending > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">Verification Portals</p>
              <p className="text-sm text-blue-700 mt-1">
                Use these official portals to verify scores:
              </p>
              <div className="flex gap-4 mt-2">
                <a 
                  href="https://ielts.idp.com/results/check-your-result" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> IELTS IDP Portal
                </a>
                <a 
                  href="https://srw.pteacademic.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> PTE Score Report
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Verifications Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredVerifications.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No verifications found</p>
              {statusFilter !== 'all' && (
                <Button variant="link" onClick={() => setStatusFilter('all')} className="mt-2">
                  Show all verifications
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Test</TableHead>
                  <TableHead>Claimed Score</TableHead>
                  <TableHead>Verification ID</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVerifications.map((v) => (
                  <TableRow key={v.id} data-testid={`verification-row-${v.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{v.student_name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{v.student_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{v.test_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold text-lg text-slate-900">
                        {v.claimed_scores?.overall}
                      </span>
                      <span className="text-xs text-slate-500 ml-1">
                        {v.test_type === 'IELTS' ? 'band' : 'pts'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {v.test_type === 'IELTS' ? v.trf_number : v.score_report_code}
                      </code>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(v.submitted_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{getStatusBadge(v.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetail(v)}
                        data-testid={`view-detail-btn-${v.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {v.status === 'pending' ? 'Verify' : 'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-ux-blue" />
              Verification Details
            </DialogTitle>
            <DialogDescription>
              {selectedVerification?.test_type} score verification for {selectedVerification?.student_name}
            </DialogDescription>
          </DialogHeader>

          {selectedVerification && (
            <div className="space-y-6">
              {/* Status Banner */}
              <div className={`rounded-lg p-4 ${
                selectedVerification.status === 'pending' ? 'bg-amber-50 border border-amber-200' :
                selectedVerification.status === 'approved' ? 'bg-emerald-50 border border-emerald-200' :
                'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedVerification.status)}
                    <span className="text-sm text-slate-600">
                      Submitted on {new Date(selectedVerification.submitted_at).toLocaleDateString()}
                    </span>
                  </div>
                  {selectedVerification.status === 'pending' && (
                    <a 
                      href={selectedVerification.test_type === 'IELTS' 
                        ? 'https://ielts.idp.com/results/check-your-result' 
                        : 'https://srw.pteacademic.com'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open {selectedVerification.test_type} Portal
                    </a>
                  )}
                </div>
              </div>

              {/* Student Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Student Name</Label>
                  <p className="font-medium">{selectedVerification.student_name || 'Unknown'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="font-medium">{selectedVerification.student_email}</p>
                </div>
              </div>

              {/* Verification Details - Copy-able */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-slate-800">Verification Details</h4>
                
                {selectedVerification.test_type === 'IELTS' && (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs text-slate-500">TRF Number</Label>
                        <p className="font-mono">{selectedVerification.trf_number}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyToClipboard(selectedVerification.trf_number, 'trf')}
                      >
                        {copiedField === 'trf' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs text-slate-500">Date of Birth</Label>
                        <p>{selectedVerification.date_of_birth}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyToClipboard(selectedVerification.date_of_birth, 'dob')}
                      >
                        {copiedField === 'dob' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </>
                )}
                
                {selectedVerification.test_type === 'PTE' && (
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-slate-500">Score Report Code</Label>
                      <p className="font-mono">{selectedVerification.score_report_code}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyToClipboard(selectedVerification.score_report_code, 'src')}
                    >
                      {copiedField === 'src' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                )}

                <div>
                  <Label className="text-xs text-slate-500">Test Date</Label>
                  <p>{selectedVerification.test_date}</p>
                </div>

                {selectedVerification.candidate_name && (
                  <div>
                    <Label className="text-xs text-slate-500">Name on Certificate</Label>
                    <p>{selectedVerification.candidate_name}</p>
                  </div>
                )}
              </div>

              {/* Claimed Scores */}
              <div>
                <h4 className="font-medium text-slate-800 mb-3">Claimed Scores</h4>
                <div className="grid grid-cols-5 gap-3">
                  {Object.entries(selectedVerification.claimed_scores || {}).map(([key, value]) => (
                    value != null && (
                      <div key={key} className="bg-slate-100 rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-slate-900">{value}</p>
                        <p className="text-xs text-slate-500 capitalize">{key}</p>
                      </div>
                    )
                  ))}
                </div>
              </div>

              {/* Verified Scores (if approved) */}
              {selectedVerification.status === 'approved' && selectedVerification.verified_scores && (
                <div>
                  <h4 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Verified Scores
                  </h4>
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(selectedVerification.verified_scores || {}).map(([key, value]) => (
                      value != null && (
                        <div key={key} className="bg-emerald-100 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-emerald-700">{value}</p>
                          <p className="text-xs text-emerald-600 capitalize">{key}</p>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Student Notes */}
              {selectedVerification.student_notes && (
                <div>
                  <Label className="text-xs text-slate-500">Student Notes</Label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg mt-1">
                    {selectedVerification.student_notes}
                  </p>
                </div>
              )}

              {/* Counselor Notes (if decided) */}
              {selectedVerification.counselor_notes && (
                <div>
                  <Label className="text-xs text-slate-500">Counselor Notes</Label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg mt-1">
                    {selectedVerification.counselor_notes}
                  </p>
                </div>
              )}

              {/* Decision Info (if already decided) */}
              {selectedVerification.status !== 'pending' && (
                <div className="text-sm text-slate-500">
                  {selectedVerification.status === 'approved' ? 'Approved' : 'Rejected'} by {selectedVerification.counselor_name || 'Admin'} on {new Date(selectedVerification.verified_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedVerification?.status === 'pending' && (
              <div className="flex gap-3 w-full">
                <Button 
                  variant="outline" 
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleStartDecision('rejected')}
                  data-testid="reject-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleStartDecision('approved')}
                  data-testid="approve-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            )}
            {selectedVerification?.status !== 'pending' && (
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decision Dialog */}
      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decisionType === 'approved' ? (
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              {decisionType === 'approved' ? 'Approve' : 'Reject'} Verification
            </DialogTitle>
            <DialogDescription>
              {decisionType === 'approved' 
                ? 'Confirm the verified scores and add any notes.'
                : 'Please provide a reason for rejection.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Verified Scores (only for approval) */}
            {decisionType === 'approved' && (
              <div>
                <Label className="text-sm font-medium">Verified Scores</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Confirm or correct the scores based on the official portal
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {['overall', 'listening', 'reading', 'writing', 'speaking'].map(key => (
                    <div key={key}>
                      <Label className="text-xs text-slate-500 capitalize">{key}</Label>
                      <Input
                        type="number"
                        step={selectedVerification?.test_type === 'IELTS' ? '0.5' : '1'}
                        value={verifiedScores[key] || ''}
                        onChange={(e) => setVerifiedScores({ ...verifiedScores, [key]: e.target.value })}
                        className="mt-1"
                        data-testid={`verified-${key}-input`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium">
                {decisionType === 'approved' ? 'Notes (Optional)' : 'Reason for Rejection *'}
              </Label>
              <Textarea
                placeholder={decisionType === 'approved' 
                  ? 'Any notes about the verification...'
                  : 'Please explain why this verification was rejected...'}
                value={decisionNotes}
                onChange={(e) => setDecisionNotes(e.target.value)}
                className="mt-1"
                rows={3}
                data-testid="decision-notes-textarea"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionDialog(false)}>
              Cancel
            </Button>
            <Button
              className={decisionType === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={handleSubmitDecision}
              disabled={submitting || (decisionType === 'rejected' && !decisionNotes)}
              data-testid="confirm-decision-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : decisionType === 'approved' ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Confirm {decisionType === 'approved' ? 'Approval' : 'Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default EnglishVerificationDashboard;
